import React from "react";
import { ThemeDrive, LanguageProvider, FeedbackProvider } from "@geotab/zenith";
import type { TSupportedLanguage } from "@geotab/zenith/dist/utils/localization/getSupportedLanguage";
import AppView from "../../app.view";
import { TranslationContext, TranslationContextType } from "./hooks/TranslationContext";
import { RouterContext, RouterContextType } from "./hooks/RouterContext";
import { ApiContext, ApiContextType } from "./hooks/ApiContext";
import { FeatureFlagContext, FeatureFlagContextType } from "./hooks/FeatureFlagContext";
import { AppContext, AppContextType } from "./hooks/AppViewContext";
import { StoreContext, StoreContextType } from "./hooks/StoreContext";
import { SyncContext, SyncContextType } from "./hooks/SyncContext";
import { useStoreSelector } from "./hooks/useStoreSelector";
import { DispatcherEvents } from "../../utils/dispatcher";
import { useDispatcherEventsSelector } from "./hooks/useDispatcherEventsSelector";
import { HosUtilsContext, HosUtilsContextType } from "./hooks/HosUtilsContext";
import { DutyStatusLogServiceContext, DutyStatusLogServiceContextType } from "./hooks/DutyStatusLogServiceContext";
import { UserServiceContext, UserServiceContextType } from "./hooks/UserServiceContext";
import { SecurityServiceContext, SecurityServiceContextType } from "./hooks/SecurityServiceContext";
import { ToastProvider } from "./hooks/ToastContext";
import { LocalizerServiceContext, LocalizerServiceContextType } from "./hooks/LocalizerServiceContext";

/**
 * A wrapper for the main page React elements which have access to the Redux store
 * @param app The AppView instance
 * @param children The main page React elements
 * @returns A React component for a Drive page
 */

const PageWrapper: React.FC<{ app: AppView; children: React.ReactNode }> = ({ app, children }) => {
    const nightMode = useStoreSelector(window.driveApp.store, state => state.data.nightMode);

    useDispatcherEventsSelector(DispatcherEvents.UserSwitch); // TODO this depend on Backbone.IEvents
    useDispatcherEventsSelector(DispatcherEvents.UserUpdateLanguage); // TODO this depend on Backbone.IEvents

    const translator = app.translationService.getTranslator();
    const language = app.translationService.getLanguage() as TSupportedLanguage;

    return (
        // TODO: Add react-redux ticket MYG-77723
        // <Provider store={window.driveApp.store}>
        <AppContext.Provider value={new AppContextType(app)}>
        <TranslationContext.Provider value={new TranslationContextType(translator)}>
        <RouterContext.Provider value={new RouterContextType(app.router)}>
        <ApiContext.Provider value={new ApiContextType(app.api)}>
        <UserServiceContext.Provider value={new UserServiceContextType(app.userService)}>
        <SecurityServiceContext.Provider value={new SecurityServiceContextType(app.securityService)}>
        <StoreContext.Provider value={new StoreContextType(app.store)}>
        <FeatureFlagContext.Provider value={new FeatureFlagContextType(app.featureFlagService)}>
        <LocalizerServiceContext.Provider value={new LocalizerServiceContextType(app.localizerService)}>
        <FeedbackProvider>
        <SyncContext.Provider value={new SyncContextType(app.sync)}>
        <HosUtilsContext.Provider value={new HosUtilsContextType(app.hosUtils)}>
        <DutyStatusLogServiceContext.Provider value={new DutyStatusLogServiceContextType(app.dutyStatusLogService)}>
        <SecurityServiceContext.Provider value={new SecurityServiceContextType(app.securityService)}>
        <UserServiceContext.Provider value={new UserServiceContextType(app.userService)}>
            <LanguageProvider language={language}>
                <ThemeDrive dark={nightMode}>
                    <ToastProvider>
                        <div>
                            {children}
                        </div>
                    </ToastProvider>
                </ThemeDrive>
            </LanguageProvider>
        </UserServiceContext.Provider>
        </SecurityServiceContext.Provider>
        </DutyStatusLogServiceContext.Provider>
        </HosUtilsContext.Provider>
        </SyncContext.Provider>
        </FeedbackProvider>
        </LocalizerServiceContext.Provider>
        </FeatureFlagContext.Provider>
        </StoreContext.Provider>
        </SecurityServiceContext.Provider>
        </UserServiceContext.Provider>
        </ApiContext.Provider>
        </RouterContext.Provider>
        </TranslationContext.Provider>
        </AppContext.Provider>
        // </Provider>
    );
};

export default PageWrapper;
